import React, { useEffect, useRef, useState } from "react";
import { connect, useDispatch } from "react-redux";
import { toast } from "react-toastify";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { Chip, Switch, FormControlLabel } from "@mui/material";
import { Close } from "@mui/icons-material";
import CommunityAdminService, {
  AdminPostFormData,
  AuctionCollectionSummary,
} from "../../services/CommunityAdminService.ts";
import { upload_cms_photo } from "../../reduxData/cms/cmsAction";

type PostType = "ARTICLE" | "COLLECTION" | "EVENT";

const POST_TYPE_COLORS: Record<PostType, string> = {
  ARTICLE: "#1976d2",
  COLLECTION: "#7b1fa2",
  EVENT: "#c62828",
};

const POST_TYPE_LABELS: Record<PostType, string> = {
  ARTICLE: "Article",
  COLLECTION: "Collection Announcement",
  EVENT: "Event Announcement",
};

const QUILL_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ["bold", "italic", "underline", "strike"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["blockquote", "link", "image"],
    ["clean"],
  ],
};

interface FormState {
  postType: PostType;
  title: string;
  content: string;
  excerpt: string;
  featured_image: string;
  tags: string[];
  meta_title: string;
  meta_description: string;
  published: boolean;
  event_date: string;
  event_location: string;
  event_url: string;
  collection_video: string;
  collection_start_date: string;
  collection_end_date: string;
  linkedCollection: AuctionCollectionSummary | null;
}

const emptyForm = (postType: PostType): FormState => ({
  postType,
  title: "",
  content: "",
  excerpt: "",
  featured_image: "",
  tags: [],
  meta_title: "",
  meta_description: "",
  published: false,
  event_date: "",
  event_location: "",
  event_url: "",
  collection_video: "",
  collection_start_date: "",
  collection_end_date: "",
  linkedCollection: null,
});

const CreateAdminPost: React.FC<{ user?: any }> = ({ user }) => {
  const dispatch = useDispatch();
  const [form, setForm] = useState<FormState>(emptyForm("ARTICLE"));
  const [newTag, setNewTag] = useState("");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [collectionSearch, setCollectionSearch] = useState("");
  const [collectionResults, setCollectionResults] = useState<
    AuctionCollectionSummary[]
  >([]);
  const [searchingCollections, setSearchingCollections] = useState(false);
  const searchTimeout = useRef<any>(null);

  const isPermission =
    (user?.role === "staff" && user?.permission?.p_staff > 1) ||
    user?.role !== "staff";

  const u = (updates: Partial<FormState>) =>
    setForm((prev) => ({ ...prev, ...updates }));

  const switchType = (postType: PostType) => {
    if (postType === form.postType) return;
    setForm((prev) => ({ ...emptyForm(postType), title: prev.title, content: prev.content }));
    setCollectionSearch("");
    setCollectionResults([]);
  };

  useEffect(() => {
    if (form.postType !== "COLLECTION") return;
    clearTimeout(searchTimeout.current);
    if (!collectionSearch.trim()) {
      setCollectionResults([]);
      return;
    }
    searchTimeout.current = setTimeout(async () => {
      setSearchingCollections(true);
      try {
        const res = await CommunityAdminService.searchAuctionCollections(
          collectionSearch,
        );
        setCollectionResults(res.data || []);
      } catch {
        setCollectionResults([]);
      } finally {
        setSearchingCollections(false);
      }
    }, 400);
    return () => clearTimeout(searchTimeout.current);
  }, [collectionSearch, form.postType]);

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const result = await upload_cms_photo(
        [file],
        localStorage.getItem("token"),
        dispatch,
      );
      if (result?.data?.photos?.[0]) {
        u({ featured_image: result.data.photos[0] });
      } else {
        toast.error("Image upload failed");
      }
    } catch {
      toast.error("Image upload failed");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const addTag = () => {
    const t = newTag.trim();
    if (t && !form.tags.includes(t)) u({ tags: [...form.tags, t] });
    setNewTag("");
  };
  const removeTag = (t: string) => u({ tags: form.tags.filter((x) => x !== t) });

  const resetForm = () => {
    setForm(emptyForm(form.postType));
    setCollectionSearch("");
    setCollectionResults([]);
  };

  const handleSubmit = async (publishNow: boolean) => {
    if (!form.title.trim()) {
      toast.error("Title is required");
      return;
    }
    if (!form.content.trim() || form.content === "<p><br></p>") {
      toast.error("Content is required");
      return;
    }

    setSaving(true);
    try {
      const payload: AdminPostFormData = {
        postType: form.postType,
        title: form.title.trim(),
        content: form.content,
        excerpt: form.excerpt.trim(),
        featured_image: form.featured_image || undefined,
        tags: form.tags,
        meta_title: form.meta_title.trim(),
        meta_description: form.meta_description.trim(),
        published: publishNow,
      };

      if (form.postType === "EVENT") {
        payload.event_date = form.event_date || null;
        payload.event_location = form.event_location.trim() || null;
        payload.event_url = form.event_url.trim() || null;
      }

      if (form.postType === "COLLECTION") {
        payload.collection_video = form.collection_video.trim() || null;
        payload.collection_start_date = form.collection_start_date || null;
        payload.collection_end_date = form.collection_end_date || null;
        payload.linkedCollection = form.linkedCollection?._id || null;
      }

      const res = await CommunityAdminService.createPost(payload);
      if (res.status) {
        toast.success(publishNow ? "Post published" : "Draft saved");
        resetForm();
      } else {
        toast.error(res.message || "Failed to create post");
      }
    } catch {
      toast.error("Failed to create post");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="container-fluid" style={{ maxWidth: 860, paddingTop: 8 }}>
      <h3 className="fw-bold mb-1">New Admin Post</h3>
      <p className="text-muted small mb-4">
        Articles, collection announcements, and event announcements authored
        by staff. These publish into the community feed.
      </p>

      {/* Type selector */}
      <div style={{ display: "flex", gap: 8, marginBottom: 24 }}>
        {(Object.keys(POST_TYPE_LABELS) as PostType[]).map((t) => (
          <button
            key={t}
            onClick={() => switchType(t)}
            style={{
              padding: "8px 18px",
              borderRadius: 8,
              border:
                form.postType === t
                  ? `2px solid ${POST_TYPE_COLORS[t]}`
                  : "1px solid #ccc",
              background: form.postType === t ? POST_TYPE_COLORS[t] : "#fff",
              color: form.postType === t ? "#fff" : "#333",
              fontWeight: 700,
              fontSize: "0.85rem",
              cursor: "pointer",
            }}
          >
            {POST_TYPE_LABELS[t]}
          </button>
        ))}
      </div>

      <div className="mb-3">
        <label className="fw-600 small mb-1 d-block">Title</label>
        <input
          className="form-control"
          placeholder="Post title"
          value={form.title}
          onChange={(e) => u({ title: e.target.value })}
          disabled={!isPermission}
        />
      </div>

      <div className="mb-3">
        <label className="fw-600 small mb-1 d-block">Excerpt</label>
        <textarea
          className="form-control"
          rows={2}
          placeholder="Short summary shown in feed cards and previews"
          value={form.excerpt}
          onChange={(e) => u({ excerpt: e.target.value })}
          disabled={!isPermission}
        />
      </div>

      <div className="mb-3">
        <label className="fw-600 small mb-1 d-block">Content</label>
        <ReactQuill
          theme="snow"
          value={form.content}
          onChange={(value) => u({ content: value })}
          modules={QUILL_MODULES}
          readOnly={!isPermission}
        />
      </div>

      <div className="row g-3 mb-3">
        <div className="col-md-6">
          <label className="fw-600 small mb-1 d-block">Featured Image</label>
          <div
            style={{
              width: "100%",
              aspectRatio: "16/9",
              background: "#f5f5f5",
              borderRadius: 8,
              overflow: "hidden",
              border: "1.5px dashed #ccc",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              position: "relative",
              marginBottom: 8,
            }}
          >
            {form.featured_image ? (
              <>
                <img
                  src={form.featured_image}
                  alt="Featured"
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
                {isPermission && (
                  <button
                    onClick={() => u({ featured_image: "" })}
                    style={{
                      position: "absolute",
                      top: 6,
                      right: 6,
                      background: "rgba(0,0,0,0.6)",
                      border: "none",
                      borderRadius: "50%",
                      width: 26,
                      height: 26,
                      color: "#fff",
                      cursor: "pointer",
                    }}
                  >
                    <Close fontSize="small" />
                  </button>
                )}
              </>
            ) : (
              <span style={{ fontSize: "0.78rem", color: "#bbb" }}>
                No image
              </span>
            )}
          </div>
          <input
            type="file"
            ref={fileInputRef}
            className="d-none"
            accept="image/png,image/jpeg,image/webp,image/gif"
            onChange={handleImageUpload}
          />
          <button
            className="btn btn-sm w-100"
            style={{
              background: "#FFD600",
              fontWeight: 700,
              border: "2px solid #000",
            }}
            onClick={() => fileInputRef.current?.click()}
            disabled={!isPermission || uploading}
          >
            {uploading ? "Uploading…" : "Upload Image"}
          </button>
        </div>

        <div className="col-md-6">
          <label className="fw-600 small mb-1 d-block">Tags</label>
          <div style={{ display: "flex", gap: 6, marginBottom: 8 }}>
            <input
              className="form-control form-control-sm"
              placeholder="Add a tag and press Enter"
              value={newTag}
              onChange={(e) => setNewTag(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  addTag();
                }
              }}
              disabled={!isPermission}
            />
            <button
              className="btn btn-sm btn-light border"
              onClick={addTag}
              disabled={!isPermission}
            >
              Add
            </button>
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {form.tags.map((t) => (
              <Chip
                key={t}
                label={t}
                size="small"
                onDelete={isPermission ? () => removeTag(t) : undefined}
              />
            ))}
          </div>

          <div className="mt-3">
            <label className="fw-600 small mb-1 d-block">
              Meta Title{" "}
              <span style={{ color: "#aaa", fontWeight: 400 }}>(SEO)</span>
            </label>
            <input
              className="form-control form-control-sm mb-2"
              value={form.meta_title}
              onChange={(e) => u({ meta_title: e.target.value })}
              disabled={!isPermission}
            />
            <label className="fw-600 small mb-1 d-block">Meta Description</label>
            <textarea
              className="form-control form-control-sm"
              rows={2}
              value={form.meta_description}
              onChange={(e) => u({ meta_description: e.target.value })}
              disabled={!isPermission}
            />
          </div>
        </div>
      </div>

      {form.postType === "EVENT" && (
        <div
          className="row g-3 mb-3 p-3"
          style={{ background: "#fff3f3", borderRadius: 10, margin: 0 }}
        >
          <div className="col-md-4">
            <label className="fw-600 small mb-1 d-block">Event Date</label>
            <input
              type="datetime-local"
              className="form-control form-control-sm"
              value={form.event_date}
              onChange={(e) => u({ event_date: e.target.value })}
              disabled={!isPermission}
            />
          </div>
          <div className="col-md-4">
            <label className="fw-600 small mb-1 d-block">Location</label>
            <input
              className="form-control form-control-sm"
              placeholder="e.g. Online, or venue name"
              value={form.event_location}
              onChange={(e) => u({ event_location: e.target.value })}
              disabled={!isPermission}
            />
          </div>
          <div className="col-md-4">
            <label className="fw-600 small mb-1 d-block">Event URL</label>
            <input
              className="form-control form-control-sm"
              placeholder="https://..."
              value={form.event_url}
              onChange={(e) => u({ event_url: e.target.value })}
              disabled={!isPermission}
            />
          </div>
        </div>
      )}

      {form.postType === "COLLECTION" && (
        <div
          className="row g-3 mb-3 p-3"
          style={{ background: "#f6f0fa", borderRadius: 10, margin: 0 }}
        >
          <div className="col-md-6">
            <label className="fw-600 small mb-1 d-block">
              Linked Auction Collection
            </label>
            {form.linkedCollection ? (
              <div style={{ marginBottom: 8 }}>
                <Chip
                  label={form.linkedCollection.name}
                  size="small"
                  color="secondary"
                  onDelete={() => u({ linkedCollection: null })}
                />
              </div>
            ) : (
              <div style={{ position: "relative" }}>
                <input
                  className="form-control form-control-sm"
                  placeholder="Search auction collections…"
                  value={collectionSearch}
                  onChange={(e) => setCollectionSearch(e.target.value)}
                  disabled={!isPermission}
                />
                {(searchingCollections || collectionResults.length > 0) && (
                  <div
                    style={{
                      position: "absolute",
                      top: "100%",
                      left: 0,
                      right: 0,
                      background: "#fff",
                      border: "1px solid #ddd",
                      borderRadius: 6,
                      zIndex: 50,
                      maxHeight: 220,
                      overflowY: "auto",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                    }}
                  >
                    {searchingCollections && (
                      <div
                        style={{
                          padding: "8px 12px",
                          fontSize: "0.8rem",
                          color: "#888",
                        }}
                      >
                        Searching…
                      </div>
                    )}
                    {collectionResults.map((c) => (
                      <div
                        key={c._id}
                        onClick={() => {
                          u({ linkedCollection: c });
                          setCollectionSearch("");
                          setCollectionResults([]);
                        }}
                        style={{
                          padding: "8px 12px",
                          cursor: "pointer",
                          borderBottom: "1px solid #f0f0f0",
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                        }}
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.background = "#f9f9f9")
                        }
                        onMouseLeave={(e) =>
                          (e.currentTarget.style.background = "#fff")
                        }
                      >
                        {c.coverImage && (
                          <img
                            src={c.coverImage}
                            alt=""
                            style={{
                              width: 32,
                              height: 32,
                              objectFit: "cover",
                              borderRadius: 4,
                            }}
                          />
                        )}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 600, fontSize: "0.8rem" }}>
                            {c.name}
                          </div>
                          {!c.isActive && (
                            <span
                              style={{ fontSize: "0.68rem", color: "#c62828" }}
                            >
                              Inactive
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                    {!searchingCollections &&
                      collectionResults.length === 0 &&
                      collectionSearch && (
                        <div
                          style={{
                            padding: "8px 12px",
                            fontSize: "0.8rem",
                            color: "#888",
                          }}
                        >
                          No results
                        </div>
                      )}
                  </div>
                )}
              </div>
            )}
            <span style={{ fontSize: "0.7rem", color: "#aaa" }}>
              Optional — links this announcement to a live auction collection.
            </span>
          </div>
          <div className="col-md-3">
            <label className="fw-600 small mb-1 d-block">Starts</label>
            <input
              type="datetime-local"
              className="form-control form-control-sm"
              value={form.collection_start_date}
              onChange={(e) => u({ collection_start_date: e.target.value })}
              disabled={!isPermission}
            />
          </div>
          <div className="col-md-3">
            <label className="fw-600 small mb-1 d-block">Ends</label>
            <input
              type="datetime-local"
              className="form-control form-control-sm"
              value={form.collection_end_date}
              onChange={(e) => u({ collection_end_date: e.target.value })}
              disabled={!isPermission}
            />
          </div>
          <div className="col-md-6">
            <label className="fw-600 small mb-1 d-block">
              Video URL{" "}
              <span style={{ color: "#aaa", fontWeight: 400 }}>(optional)</span>
            </label>
            <input
              className="form-control form-control-sm"
              placeholder="https://..."
              value={form.collection_video}
              onChange={(e) => u({ collection_video: e.target.value })}
              disabled={!isPermission}
            />
          </div>
        </div>
      )}

      <div
        className="d-flex align-items-center justify-content-between mt-4 pt-3"
        style={{ borderTop: "1px solid #eee" }}
      >
        <FormControlLabel
          control={<Switch checked={form.published} onChange={(e) => u({ published: e.target.checked })} />}
          label={form.published ? "Will publish immediately" : "Save as draft"}
        />
        <div style={{ display: "flex", gap: 10 }}>
          <button
            className="btn btn-sm btn-light border"
            onClick={() => handleSubmit(false)}
            disabled={!isPermission || saving}
          >
            Save Draft
          </button>
          <button
            className="btn btn-sm"
            style={{
              background: "#FFD600",
              fontWeight: 700,
              border: "2px solid #000",
              padding: "6px 20px",
            }}
            onClick={() => handleSubmit(true)}
            disabled={!isPermission || saving}
          >
            {saving ? "Saving…" : "Publish"}
          </button>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state: any) => ({ user: state.auth.user });
export default connect(mapStateToProps)(CreateAdminPost);
